import type { MaybeRef } from '@vueuse/core'
import { unref } from 'vue'

/**
 * @returns Are the two given dates equal (only compares date, month and year) ?
 */
export function isDateEqual(dateLeft: Date, dateRight: Date) {
  return (
    dateLeft.getDate() === dateRight.getDate() &&
    dateLeft.getMonth() === dateRight.getMonth() &&
    dateLeft.getFullYear() === dateRight.getFullYear()
  )
}

/**
 * Reset a date's time info.
 * @returns A new date
 */
export function resetDateTime(date: Date) {
  const newDate = new Date(date)
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

/**
 * Is the given date in between the two other given dates
 */
export function isDateInRange(date: Date, min?: MaybeRef<Date>, max?: MaybeRef<Date>) {
  const minDate = unref(min)
  const maxDate = unref(max)

  const isMinDateOk = minDate
    ? resetDateTime(date).getTime() >= resetDateTime(minDate).getTime()
    : true

  const isMaxDateOk = maxDate
    ? resetDateTime(date).getTime() <= resetDateTime(maxDate).getTime()
    : true

  return isMinDateOk && isMaxDateOk
}

/** Add query params to a relative URL */
export function addQueryParamsToRelativeURL(
  relativeURL: string,
  queryParams: { [key: string]: string },
): string {
  const url = new URL(relativeURL, window.location.origin)

  for (const param in queryParams) {
    url.searchParams.set(param, queryParams[param])
  }

  return url.pathname + url.search + url.hash
}

/**
 * This function allows to calcul the price rounded (rounded to the closest 2 decimal places) from a given prices
 * and percentage to pay.
 *
 * @param price provide price (can be any value)
 * @param percentageToPay must be contains 0-100
 * @returns 2 things: the price to pay now and the price to pay later
 */
export function calculPercentageToPay(price: number, percentageToPay: number): [number, number] {
  // Control parameters
  if (percentageToPay > 100) {
    percentageToPay = 100
  }
  if (percentageToPay < 0) {
    percentageToPay = 100
  }
  if (percentageToPay === 0) {
    return [0, Math.round(price * 100) / 100]
  }
  // Math.max is use to avoid the "negative zero" (-0), because Js is a shit language
  const payNow = Math.max(0, Math.round(price * (percentageToPay / 100) * 100) / 100)
  const payRemain = Math.max(0, Math.round((price - payNow) * 100) / 100)
  return [payNow, payRemain]
}

/**
 * This function allows to compare 2 different strings array
 *
 * @param arrOne array one
 * @param arrTwo array two
 * @returns boolean: when arrays are identical return true, otherwise false
 */
export function isIdenticalArrays(arrOne: string[], arrTwo: string[]): boolean {
  // Check if the lengths of the arrays are the same
  if (arrOne.length !== arrTwo.length) {
    return false
  }
  // Sort both arrays to ensure the elements are in the same order
  arrOne.sort()
  arrTwo.sort()
  // Compare each element of the sorted arrays
  for (let i = 0; i < arrOne.length; i++) {
    if (arrOne[i] !== arrTwo[i]) {
      return false
    }
  }
  // If all elements are the same, the arrays are identical
  return true
}
